import { useEffect, useState, useCallback } from "react";
import { Link2, Loader2, ShieldCheck, AlertTriangle, RefreshCw, Save, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
}

export function AdminAgencyLinks() {
  const [states, setStates] = useState([]);
  const [alertEmail, setAlertEmail] = useState("");
  const [emailDraft, setEmailDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [togglingState, setTogglingState] = useState(null);

  const load = useCallback(() => {
    api.agencyOverview().then((d) => {
      setStates(d.states || []);
      setAlertEmail(d.alert_email || "");
      setEmailDraft(d.alert_email || "");
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const runCheck = async () => {
    setRunning(true);
    try {
      const r = await api.runLinkHealthCheck();
      toast.success(`Checked ${r.checked} links — ${r.unhealthy} unhealthy${r.newly_down ? `, ${r.newly_down} newly down` : ""}`);
      load();
    } catch { toast.error("Health check failed"); }
    finally { setRunning(false); }
  };

  const togglePartner = async (s) => {
    setTogglingState(s.jurisdiction);
    try {
      await api.setAgencyPartner(s.jurisdiction, !s.official_partner);
      toast.success(`${s.jurisdiction} ${!s.official_partner ? "marked as Official Partner" : "removed as partner"}`);
      setStates((prev) => prev.map((x) => x.jurisdiction === s.jurisdiction ? { ...x, official_partner: !s.official_partner } : x));
    } catch { toast.error("Could not update"); }
    finally { setTogglingState(null); }
  };

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      await api.setLinkAlertEmail(emailDraft.trim());
      setAlertEmail(emailDraft.trim());
      toast.success("Alert email saved");
    } catch { toast.error("Could not save email"); }
    finally { setSavingEmail(false); }
  };

  if (loading) return <div className="py-8 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>;

  const totalDown = states.reduce((a, s) => a + (s.unhealthy || 0), 0);
  const partners = states.filter((s) => s.official_partner).length;

  return (
    <div className="mt-10" data-testid="admin-agency-links">
      <h2 className="font-serif text-2xl text-forest mb-3 flex items-center gap-2"><Link2 size={20} /> State agency links</h2>

      {/* Controls */}
      <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end bg-white border border-saddle/20 p-4">
        <div>
          <label className="block text-xs text-charcoal/60">Broken-link alert email
            <div className="flex gap-2 mt-1">
              <div className="flex-1 flex items-center gap-2 border border-saddle/25 px-2 py-1.5">
                <Mail size={14} className="text-saddle shrink-0" />
                <input value={emailDraft} onChange={(e) => setEmailDraft(e.target.value)} placeholder="alerts@example.com" className="w-full outline-none text-sm" data-testid="alert-email-input" />
              </div>
              <button onClick={saveEmail} disabled={savingEmail || emailDraft.trim() === alertEmail} className="bg-forest text-bone px-4 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="save-alert-email">
                {savingEmail ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
              </button>
            </div>
          </label>
          <p className="text-xs text-charcoal/45 mt-2">{partners} partner state(s) · {totalDown} link(s) currently down across {states.length} states.</p>
        </div>
        <button onClick={runCheck} disabled={running} className="bg-blaze text-white px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid="run-health-check">
          {running ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Run link check now
        </button>
      </div>

      {/* State table */}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="agency-state-table">
          <thead>
            <tr className="border-b border-saddle/15 text-left text-charcoal/50 hr-overline text-[10px]">
              <th className="p-2">State</th><th className="p-2">Agency</th><th className="p-2">Link health</th>
              <th className="p-2">Last checked</th><th className="p-2">Official Partner</th>
            </tr>
          </thead>
          <tbody>
            {states.map((s) => (
              <tr key={s.jurisdiction} className="border-b border-saddle/10" data-testid={`agency-row-${s.jurisdiction}`}>
                <td className="p-2 font-semibold text-forest whitespace-nowrap">{s.jurisdiction}</td>
                <td className="p-2 text-charcoal/60">{s.agency_name}</td>
                <td className="p-2">
                  {s.unhealthy > 0 ? (
                    <span className="inline-flex items-center gap-1 text-blaze font-semibold"><AlertTriangle size={13} /> {s.unhealthy} down</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[#2f7d4f]"><ShieldCheck size={13} /> All healthy</span>
                  )}
                </td>
                <td className="p-2 text-charcoal/50 whitespace-nowrap">{fmtDate(s.last_checked_at)}</td>
                <td className="p-2">
                  <button
                    onClick={() => togglePartner(s)} disabled={togglingState === s.jurisdiction}
                    className={`px-3 py-1 text-xs font-semibold border transition-colors disabled:opacity-50 ${s.official_partner ? "bg-forest text-bone border-forest" : "border-saddle/30 text-charcoal/60 hover:border-forest"}`}
                    data-testid={`partner-toggle-${s.jurisdiction}`}
                  >
                    {togglingState === s.jurisdiction ? "…" : s.official_partner ? "Official Partner ✓" : "Mark partner"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
